import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import type { PersonSummary } from "@scheduler/shared";
import { type ReactNode, useEffect, useState } from "react";

import { ApiError, useSchedulerApi } from "./api/scheduler";
import { Approvals } from "./approvals/Approvals";
import { TeamCalendar } from "./calendar/TeamCalendar";
import { Deployment, LegacyFieldLink } from "./field/Deployment";
import { fieldpathUrl, helpUrl, ROUTE_HELP } from "./links";
import { FieldWork } from "./field/FieldWork";
import { HrTab } from "./hr/HrTab";
import { navigate, rememberReturnPath, type Route, useRoute } from "./router";
import { loginRequest } from "./auth/config";
import { actingAs, setActingAs } from "./auth/actingAs";
import { devActor } from "./auth/identity";
import { ActAsBanner, ActAsPicker } from "./ActAs";
import { DevActorBar } from "./DevActorBar";
import { HelpQ } from "./HelpQ";
import { ReportProblem } from "./ReportProblem";
import { MyRequests } from "./requests/MyRequests";
import { WorkFromHome } from "./wfh/WorkFromHome";
import { useRefreshOnReturn } from "./refreshOnReturn";
import { UpdateBar } from "./UpdateBar";


/**
 * A face, no rules — every decision lives in the API. In local development
 * with a dev actor set, the Entra sign-in is skipped (see auth/identity.ts).
 */
export function App() {
  // Literal DEV check so production builds drop the dev path and DevActorBar entirely.
  const dev = import.meta.env.DEV ? devActor() : null;
  if (dev) return <Scheduler dev={dev} />;
  return (
    <>
      <UnauthenticatedTemplate>
        <Header dev={null} />
        <main>
          <SignIn />
        </main>
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <Scheduler dev={null} />
      </AuthenticatedTemplate>
      {/* A newer build is being served (punch list F173). */}
      <UpdateBar />
    </>
  );
}

/**
 * The header, styled as Fieldpath's (Jeremy, 21 Oct 2026): the ERP and Canopy marks, the screens
 * as navigation IN the bar with a lime underline on the current one, and Report a problem and
 * Sign out on the right. CANOPY is the company's name for Fieldpath and this app together; the
 * small "Scheduler" beside it says which of the two you are in. Until then this was a text brand
 * with the screens as tabs under the bar.
 *
 * `nav` is the screens, passed in once the caller is known: before that (signing in, loading,
 * refused) the bar carries only the marks and the controls.
 */
function Header({ dev, nav }: { dev: ReturnType<typeof devActor> | null; nav?: ReactNode }) {
  const home = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate({ kind: "calendar", month: null, date: null, view: null });
  };
  return (
    <header className="app">
      <div className="header-inner">
        <div className="nav-row">
          {/* Both halves go home, as in Fieldpath. Links, so a middle-click opens home too. */}
          <div className="brand">
            <a className="brand-erp" href="/" onClick={home} title="Scheduler home">
              <img className="erp-mark" src="/erp-logo.png" alt="Energy Renewal Partners" />
            </a>
            <span className="brand-rule" aria-hidden="true" />
            <a className="brand-canopy" href="/" onClick={home} title="Scheduler home" aria-label="Canopy Scheduler home">
              <img className="product-mark" src="/canopy-mark.png" alt="" />
              <img className="canopy-word" src="/canopy-word.png" alt="Canopy" />
              <span className="brand-app">Scheduler</span>
            </a>
          </div>
          {nav ? <nav className="nav" aria-label="Screens">{nav}</nav> : null}
        </div>
        <div className="header-right">
          {/* Inside AuthenticatedTemplate: it needs a token to post, and an
              unauthenticated visitor has nothing to report yet. Note the route
              itself does NOT require the roster link - see api/routes/reports.ts. */}
          <AuthenticatedTemplate><ReportProblem /></AuthenticatedTemplate>
          {dev ? <DevActorBar actor={dev} /> : <AuthenticatedTemplate><SignOut /></AuthenticatedTemplate>}
        </div>
      </div>
    </header>
  );
}

function SignIn() {
  const { instance } = useMsal();
  return (
    <button
      type="button"
      className="primary narrow"
      onClick={() => {
        // Entra returns to the site root, so the intended path is stashed
        // first or a shared link is lost by signing in successfully.
        rememberReturnPath();
        void instance.loginRedirect(loginRequest);
      }}
    >
      Sign in with Microsoft
    </button>
  );
}

function SignOut() {
  const { instance } = useMsal();
  return (
    <button type="button" className="ghost" onClick={() => void instance.logoutRedirect()}>
      Sign out
    </button>
  );
}

/** Who the API says the caller is, then the header with the screens, then the screen. */
function Scheduler({ dev }: { dev: ReturnType<typeof devActor> | null }) {
  const api = useSchedulerApi();
  const [me, setMe] = useState<PersonSummary | null>(null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  // The URL is the navigation state, not a useState: a screen you cannot
  // paste to somebody is a screen that only exists for whoever is looking at
  // it, and holding it in React also breaks refresh and Back.
  const route = useRoute();
  const [pendingCount, setPendingCount] = useState(0);

  // Only somebody who may decide something has the tab, so only they ask what is waiting.
  const approver = me?.isApprover === true;
  // Asked again on coming back to the tab (punch list F172), whatever is open: it is a count.
  const backCount = useRefreshOnReturn({ whileEditing: true });
  useEffect(() => {
    if (!approver) {
      setPendingCount(0);
      return;
    }
    // Requests only: field work is booked when saved and waits on nobody (27 Oct 2026).
    api.pending().then((requests) => setPendingCount(requests.length), () => setPendingCount(0));
  }, [api, approver, backCount]);

  useEffect(() => {
    let cancelled = false;
    api.me().then(
      (person) => { if (!cancelled) setMe(person); },
      (e: unknown) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? { code: e.code, message: e.message } : { code: "NETWORK", message: "Could not reach the API." });
      },
    );
    return () => { cancelled = true; };
  }, [api]);

  if (error) {
    // Acting as somebody the API now refuses (they left, or the note was lost) must not strand
    // HR on an error with no way back to themselves.
    const stuck = actingAs();
    return (
      <>
        <Header dev={dev} />
        <main>
          <div className="note">
            <strong>{error.code === "NOT_LINKED" ? "Not linked yet." : "Can't continue."}</strong> {error.message}
            {stuck ? (
              <>
                {" "}
                <button type="button" className="ghost" onClick={() => { setActingAs(null); window.location.reload(); }}>
                  Stop acting as {stuck.name}
                </button>
              </>
            ) : null}
          </div>
        </main>
      </>
    );
  }
  if (!me) {
    return (
      <>
        <Header dev={dev} />
        <main>
          <p className="muted">Checking who you are…</p>
        </main>
      </>
    );
  }

  const tab = (kind: Route["kind"], on: boolean, label: ReactNode, go: () => void) => (
    <button key={kind} type="button" className={on ? "on" : ""} aria-current={on ? "page" : undefined} onClick={go}>
      {label}
    </button>
  );
  // The Team calendar first: it is where the app opens (Jeremy, 21 Oct 2026).
  const nav = (
    <>
      {tab("calendar", route.kind === "calendar", "Team calendar",
        () => navigate({ kind: "calendar", month: null, date: null, view: null }))}
      {tab("mine", route.kind === "mine", "My requests", () => navigate({ kind: "mine" }))}
      {tab("wfh", route.kind === "wfh", "Work from home", () => navigate({ kind: "wfh", month: null }))}
      {/* HR and Deltek supervisors only (punch list S16, S17, 30 Oct 2026): everybody else had an
          empty tab. While HR acts as someone it follows THAT person. A courtesy like the HR tab:
          every decision re-checks decideCapacity. */}
      {me.isApprover
        ? tab("approvals", route.kind === "approvals",
          <>Approvals{pendingCount ? <span className="count">{pendingCount}</span> : null}</>,
          () => navigate({ kind: "approvals" }))
        : null}
      {/* A deployment keeps Field work current: it is a page within that screen. */}
      {tab("field", route.kind === "field" || route.kind === "deployment" || route.kind === "legacyField", "Field work", () => navigate({ kind: "field" }))}
      {/* HR Role only. Hiding it is a courtesy, not the control: every HR route in the API
          re-checks hr_approver. */}
      {me.isHr && !me.actingAs ? tab("hr", route.kind === "hr", "HR", () => navigate({ kind: "hr" })) : null}
      {/* Back to Fieldpath. A LINK, never an iframe (MSAL renews tokens through a hidden iframe
          needing third-party cookies, which incognito blocks), and SAME TAB, as Fieldpath's is. The
          sign-in is per browser since 30 Oct 2026 (auth/config.ts), per tab until then. Never `on` or
          `aria-current`: you are never "in" Fieldpath from here. */}
      <a className="nav-external" href={fieldpathUrl}>
        {/* "Projects", not "Fieldpath": people know both apps as Canopy (punch list S14, 29 Oct 2026). */}
        Projects<span aria-hidden="true"> ↗</span>
      </a>
      {/* Admins: through to Fieldpath's admin page, where the Deltek refresh lives (Jeremy, 21 Oct
          2026: in the header, as Fieldpath has it; it sat beside the name until then). This app
          has no copy of that page. The flag only decides whether the link is OFFERED: Fieldpath
          re-checks on every admin route. */}
      {me.isAdmin ? (
        <a className="nav-external" href={`${fieldpathUrl}/admin`}>
          Admin<span aria-hidden="true"> ↗</span>
        </a>
      ) : null}
      {/* Canopy's help, which lives in Fieldpath (links.ts, helpUrl). A link like the two above,
          same tab for the same reason. Only here, once the caller is known: somebody not linked
          to Deltek sees no navigation at all, as in Fieldpath. */}
      <a className="nav-external" href={helpUrl()}>
        Help<span aria-hidden="true"> ↗</span>
      </a>
    </>
  );

  // /hr without HR Role, and /approvals for somebody with nothing to decide, show the Team
  // calendar instead: a screen they cannot use is not a screen worth showing.
  const showsCalendarInstead =
    (route.kind === "hr" && !(me.isHr && !me.actingAs)) || (route.kind === "approvals" && !me.isApprover);

  return (
    <>
      <Header dev={dev} nav={nav} />
      <main>
        {/* While HR acts as someone: on every screen, impossible to miss (TIME_OFF_PLAN). */}
        {me.actingAs ? <ActAsBanner me={me} wfh={route.kind === "wfh"} /> : null}
        {/* Just the name, and HR's Act as. A person's accrual rate and how their requests route
            are facts about asking for PTO, so they belong in the PTO request. */}
        <div className="lede">
          <div className="lede-who">
            <strong>{me.name}</strong>
            {/* HR only, and not from inside an acting-as session. The API decides regardless. */}
            {me.isHr && !me.actingAs ? (
              <>
                {" · "}
                <ActAsPicker me={me} />
                <HelpQ screen="scheduler-actas" label="Help with Act as" />
              </>
            ) : null}
          </div>
          {/* The screen's ?, on every screen: Fieldpath's article for it, in this tab. Somebody
              without HR Role on /hr is shown the calendar, so they get the calendar's help. */}
          <a
            className="help-link"
            href={helpUrl(showsCalendarInstead ? "scheduler-calendar" : ROUTE_HELP[route.kind])}
          >
            <span className="help-q" aria-hidden="true">?</span>
            <span>
              Help for this screen<span className="arrow" aria-hidden="true"> ↗</span>
            </span>
          </a>
        </div>
        {route.kind === "mine" ? <MyRequests me={me} /> : null}
        {route.kind === "wfh" ? <WorkFromHome me={me} month={route.month} /> : null}
        {route.kind === "approvals" ? (
          me.isApprover
            ? <Approvals onCountChange={setPendingCount} />
            : <TeamCalendar route={{ kind: "calendar", month: null, date: null, view: null }} />
        ) : null}
        {route.kind === "field" ? <FieldWork me={me} /> : null}
        {route.kind === "deployment" ? <Deployment id={route.id} /> : null}
        {route.kind === "legacyField" ? <LegacyFieldLink requestId={route.requestId} /> : null}
        {route.kind === "calendar" ? <TeamCalendar route={route} /> : null}
        {/* Somebody without HR Role who follows an /hr link sees the calendar instead: a screen
            they cannot use is not a screen worth showing. */}
        {route.kind === "hr" ? (me.isHr && !me.actingAs ? <HrTab /> : <TeamCalendar route={{ kind: "calendar", month: null, date: null, view: null }} />) : null}
      </main>
    </>
  );
}
